import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpParams } from '@angular/common/http';
import moment from 'moment';

import { environment } from '../../../environments/environment';
import { FlashService } from '../../components/flash/flash.service';
import { User } from '../user';
import { Hospital } from '../../hospitals/hospital';

interface Filter {
  value: any;
}

interface Event {
  first?: number;
  rows?: number;
  filters?: { [key: string]: Filter };
  originalEvent?: any;
  sortField?: string;
  sortOrder?: number;
  value?: { label: string, value: any };
}

interface TableState {
  first: number;
  rows: number;
  sortField?: string;
  sortOrder?: number;
}

@Component({
    selector: 'app-users-view',
    templateUrl: './b2c.component.html',
    styleUrls: [
        './b2c.component.scss'
    ],
    standalone: false
})
export class UsersB2CComponent implements OnInit, OnDestroy {
  // first_name, last_name and email identify a user, so they are never written to
  // session storage; only the non-identifying dropdown selections are restored.
  static readonly PERSISTED_CRITERIA = ['hospital_id', 'confirmed_at', 'created_at'];
  static readonly STORAGE_KEY = 'searchCriteriaB2C';

  users: User[];
  hospitals: Hospital[];
  hospitalMapper: any = {};
  hospitalFilterList = [];
  selectedUser: User;
  selectedHospital: any;
  selectedIsConfirmed: any;
  selectedMonth: any;
  loading = false;
  loadFailed = false;
  first = 0;
  rows = 10;
  totalRecords = 0;
  page = 1;
  searchCriteria: any[] = [];

  private tableState: TableState = { first: 0, rows: 10 };
  private criteriaRestored = false;
  private latestRequest = 0;

  placeholders = {
    hospital_id: ' ',
    confirmed_at: ' ',
    created_at: ' ',
    first_name: 'First Name',
    last_name: 'Last Name',
    email: 'Email'
  };

  confirmedList = [{value: true, title: 'Yes', label: 'confirmed_at'},
    {value: false, title: 'No', label: 'confirmed_at'}];

  monthList = [
    {value: 'january', title: 'January', label: 'created_at'},
    {value: 'february', title: 'February', label: 'created_at'},
    {value: 'march', title: 'March', label: 'created_at'},
    {value: 'april', title: 'April', label: 'created_at'},
    {value: 'may', title: 'May', label: 'created_at'},
    {value: 'june', title: 'June', label: 'created_at'},
    {value: 'july', title: 'July', label: 'created_at'},
    {value: 'august', title: 'August', label: 'created_at'},
    {value: 'september', title: 'September', label: 'created_at'},
    {value: 'october', title: 'October', label: 'created_at'},
    {value: 'november', title: 'November', label: 'created_at'},
    {value: 'december', title: 'December', label: 'created_at'}
  ];

  tableConfig = {
    columns: {
      first_name: {
        title: 'First Name'
      },

      last_name: {
        title: 'Last Name'
      },

      email: {
        title: 'Email'
      },

      hospital_name: {
        title: 'Organization'
      },

      is_confirmed: {
        title: 'Confirmed?'
      },

      created_on: {
        title: 'Created On',
        width: '200px'
      }
    },

    actions: {
      add: false,
      edit: false,
      delete: false
    }
  };

  // The table displays computed columns; these are the database columns the API sorts on.
  sortColumns = {
    first_name: 'first_name',
    last_name: 'last_name',
    email: 'email',
    hospital_name: 'hospital_id',
    is_confirmed: 'confirmed_at',
    created_on: 'created_at'
  };

  keys = Object.keys(this.tableConfig.columns);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    protected http: HttpClient,
    private flashService: FlashService
  ) {}

  ngOnInit() {
    this.route.data.subscribe((data) => {
      this.hospitals = data.hospitals.data;
      this.hospitalFilterList = [];

      this.hospitalMapper = this.hospitals.reduce((map, hospital) => {
        map[hospital.id] = hospital.name;

        this.hospitalFilterList.push({
          value: hospital.id,
          title: hospital.name,
          label: 'hospital_id'
        });

        return map;
      }, {});

      // Users with no organization are displayed as "B2C"; the API matches them on 'none'.
      this.hospitalFilterList.unshift({value: 'none', title: 'B2C', label: 'hospital_id'});
    });
  }

  ngOnDestroy() {
    const persistable = this.searchCriteria.filter(criteria =>
      Object.keys(criteria).every(key => UsersB2CComponent.PERSISTED_CRITERIA.includes(key))
    );

    sessionStorage.setItem(UsersB2CComponent.STORAGE_KEY, JSON.stringify(persistable));
  }

  resetPlaceHolder(filterName) {
    this.placeholders[filterName] = ' ';
  }

  addOrUpdateSearchCriteria(searchTerm, value) {
    const existingEntry = this.searchCriteria.find(entry => entry[searchTerm] !== undefined);
    if (existingEntry) {
      existingEntry[searchTerm] = value;
    } else {
      this.searchCriteria.push({[searchTerm]: value});
    }
  }

  removeSearchCriteria(filterName) {
    const index = this.searchCriteria.findIndex(entry => entry[filterName] !== undefined);
    if (index !== -1) {
      this.searchCriteria.splice(index, 1);
    }
  }

  appendPagination() {
    this.page = Math.floor(this.tableState.first / this.tableState.rows) + 1;
    this.addOrUpdateSearchCriteria('page', this.page);
    this.addOrUpdateSearchCriteria('per_page', this.tableState.rows);
  }

  appendSort() {
    const column = this.sortColumns[this.tableState.sortField];
    if (!column) {
      this.removeSearchCriteria('sort');
      this.removeSearchCriteria('order');
      return;
    }

    this.addOrUpdateSearchCriteria('sort', column);
    this.addOrUpdateSearchCriteria('order', this.tableState.sortOrder === -1 ? 'desc' : 'asc');
  }

  pageChange(event, filterName?: string) {
    if (event.value === null && filterName) {
      this.removeSearchCriteria(filterName);
      this.resetPlaceHolder(filterName);
    }
    this.loadUsers(event);
  }

  processEventFilters(event?: Event) {
    // Depending on the filter clicked, the event object will have a different structure
    if (event.filters) {
      Object.entries(event.filters).forEach(([key, filter]) => {
        const value = filter?.value;

        // A cleared filter has to drop its criterion, otherwise the previous search
        // term keeps being sent even though the input is empty.
        if (value === null || value === undefined || value === '') {
          this.removeSearchCriteria(key);
        } else {
          this.addOrUpdateSearchCriteria(key, value);
        }
      });
    }
    if (event.value) {
      this.addOrUpdateSearchCriteria(event.value.label, event.value.value);
    }
  }

  buildParams(): HttpParams {
    this.appendPagination();
    this.appendSort();

    let params = new HttpParams();
    this.searchCriteria.forEach((criteria) => {
      Object.entries(criteria).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, value as any);
        }
      });
    });
    return params;
  }

  loadUsers(event?: Event) {
    this.restoreSavedCriteria();
    this.applyEvent(event);

    const params = this.buildParams();
    const requestId = ++this.latestRequest;

    this.loading = true;
    this.http.get(`${environment.apiurl}/users/b2c`, { observe: 'response', params }).subscribe({
      next: (response: any) => {
        // A slower earlier request must not overwrite the results of a newer one.
        if (requestId !== this.latestRequest) {
          return;
        }

        this.loading = false;
        this.loadFailed = false;
        this.totalRecords = response.body.meta['x-total-count'];
        this.users = this.mutateUsers(response.body.data);
      },
      error: () => {
        if (requestId !== this.latestRequest) {
          return;
        }

        this.loading = false;
        this.loadFailed = true;
        this.users = [];
        this.totalRecords = 0;
        this.flashService.add({
          type: 'danger',
          message: '<strong>Error!</strong> Could not load B2C users. Please try again.'
        });
      }
    });
  }

  private applyEvent(event?: Event) {
    if (!event) {
      return;
    }

    if (event.rows !== undefined) {
      // The table's lazy-load event owns pagination, sorting and the column filters.
      this.tableState = {
        first: event.first || 0,
        rows: event.rows,
        sortField: event.sortField,
        sortOrder: event.sortOrder
      };
      this.first = this.tableState.first;
    } else {
      // A dropdown change carries only its own value, so keep the page size and
      // ordering the table is currently showing and return to the first page.
      this.tableState = { ...this.tableState, first: 0 };
      this.first = 0;
    }

    this.processEventFilters(event);
  }

  private restoreSavedCriteria() {
    if (this.criteriaRestored) {
      return;
    }
    this.criteriaRestored = true;

    const savedSearchCriteria = sessionStorage.getItem(UsersB2CComponent.STORAGE_KEY);
    if (!savedSearchCriteria) {
      return;
    }

    this.searchCriteria = JSON.parse(savedSearchCriteria);
    this.processSavedCriteria('hospital_id', 'hospitalFilterList', 'selectedHospital');
    this.processSavedCriteria('confirmed_at', 'confirmedList', 'selectedIsConfirmed');
    this.processSavedCriteria('created_at', 'monthList', 'selectedMonth');
  }

  processSavedCriteria(criterionKey: string, filterListKey: string, selectedKey: string) {
    const criterion = this.searchCriteria.find(criteria => criteria[criterionKey] !== undefined);
    if (criterion) {
      const match = this[filterListKey].find(entry => entry.value === criterion[criterionKey]);
      if (match) {
        this[selectedKey] = match.title;
        this.placeholders[criterionKey] = match.title;
      }
    }
  }

  public onRowSelect(event) {
    this.router.navigate(['/b2c', event.data.id]);
  }

  private mutateUsers(users: User[]) {
    users.forEach(user => {
      user['hospital_name'] = this.hospitalMapper[user.hospital_id] || 'B2C';
      user['created_on'] = moment(user['created_at']).format('LLL');
    });
    return users;
  }
}
